<script setup lang="ts">
import VListCardRow from "@verbleif/lib/src/components/ListCard/VListCardRow.vue";
import VSwitch from "@verbleif/lib/src/components/Switch/VSwitch.vue";
import VTag from "@verbleif/lib/src/components/Tag/VTag.vue";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * One line of a debug card: the label on the left and on the right a value, a Ja/Nee label, a tag or a switch.
 * An empty value shows a dash.
 */
const {
  label,
  value = undefined,
  mono = false,
  yesNo = undefined,
  tag = undefined,
  tagVariant = "is-gray",
  help = undefined,
  isSwitch = false,
} = defineProps<{
  label: string
  value?: string | number | null
  /** Shows the value in a monospaced font, for ids, IRIs and addresses. */
  mono?: boolean
  /** Shows Ja (green) or Nee (grey) instead of a value. */
  yesNo?: boolean
  /** Shows this text as a tag instead of a value. */
  tag?: string
  tagVariant?: string
  help?: string
  /** Shows a switch bound to `v-model` instead of a value. */
  isSwitch?: boolean
}>();

defineSlots<{
  /** Replaces the value on the right. */
  default?: () => unknown
}>();

const model = defineModel<boolean>({ default: false });

const { t } = useI18n();
const switchId = useId();

const displayValue = computed(() => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
});
</script>

<template>
  <VListCardRow
    :title="label"
    :help="help"
    :control-id="isSwitch ? switchId : undefined"
    wrap
  >
    <template #trailing>
      <slot>
        <VSwitch
          v-if="isSwitch"
          :id="switchId"
          v-model="model"
          :name="switchId"
          :aria-label="label"
        />
        <VTag
          v-else-if="yesNo !== undefined"
          :text="yesNo ? t('debug_tools.yes') : t('debug_tools.no')"
          :variant="yesNo ? 'is-success' : 'is-gray'"
          icon-less
        />
        <VTag v-else-if="tag !== undefined" :text="tag" :variant="tagVariant" icon-less />
        <span
          v-else
          class="min-w-0 break-all text-right text-sm text-gray-900 dark:text-gray-100"
          :class="{ 'font-mono text-xs': mono }"
        >
          {{ displayValue }}
        </span>
      </slot>
    </template>
  </VListCardRow>
</template>
